/**
 * @file reservation-calendar.js
 * @description 予約カレンダー（Vite / プレーンJS, ESM）
 * - 表示範囲: 当月〜7ヶ月後（合計8ヶ月）※ monthsAhead=7 を既定値に
 * - 年月セレクトは1つ
 * - 過去日（今日より前）は非活性（バッジ/リンクなし）、当日は含める
 * - 空室状態: "○"|"△"|"×"（△/○ のみリンク）
 * - 切替時はローディング演出（最小400ms）＋世代管理
 */

/** @typedef {"○"|"△"|"×"} AvailabilityStatus */
/**
 * @typedef AvailabilityItem
 * @property {string} date   - ISO日付(YYYY-MM-DD)
 * @property {AvailabilityStatus} status
 * @property {string=} url   - △/○ のときのリンク先
 * @property {string=} label - リンクラベル（省略時 "空室状況"）
 */

/** デフォルトリンクラベル */
export const DEFAULT_LINK_LABEL = "空室状況";

/** ステータス → 表示メタ */
function statusMeta(info) {
  switch (info.availableCount) {
    case 1:
      return { cls: "ok", aria: "空室あり", img: "circle" };
    default:
      return { cls: "ng", aria: "空室なし", img: "cross" };
  }
}

/** "YYYY-MM-DD" を生成 */
export const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** その日の00:00 */
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** 月メタ */
function getMonthMeta(year, monthIndex) {
  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex + 1, 0);
  const startPad = first.getDay(); // 0=Sun
  const totalDays = last.getDate();
  return { first, last, startPad, totalDays };
}

/** availability配列 → Map(date=>item) */
function toAvailabilityMap(rows) {
  const map = new Map();
  for (const r of rows || []) {
    if (r?.date) map.set(r.date, r);
  }
  return map;
}

/** 範囲を1日固定のDateで返す（当月 & +monthsAhead ヶ月後） */
function getRangeLimits(today, monthsAhead) {
  const min = new Date(today.getFullYear(), today.getMonth(), 1);
  const max = new Date(today.getFullYear(), today.getMonth() + monthsAhead, 1);
  return { min, max };
}

/**
 * @typedef ReservationCalendarOptions
 * @property {HTMLElement} calendarEl - カレンダー描画先（グリッド）
 * @property {HTMLSelectElement} ymSelectEl - 年月セレクト（1つ）
 * @property {HTMLButtonElement} prevBtnEl - 前月ボタン
 * @property {HTMLButtonElement} nextBtnEl - 次月ボタン
 * @property {HTMLElement=} loadingEl - ローディングオーバーレイ（省略可）
 * @property {HTMLSelectElement=} adultsSelectEl - 大人の人数セレクト（省略可）
 * @property {HTMLSelectElement=} childrenSelectEl - 小学生の人数セレクト（省略可）
 * @property {HTMLSelectElement=} infantsSelectEl - 幼児の人数セレクト（省略可）
 * @property {number=} monthsAhead - 先の表示月数（既定 7＝当月含め8ヶ月）
 * @property {Date=} today - 今日扱いの基準日（テスト用、既定 new Date()）
 * @property {AvailabilityItem[]=} availability - 初期データ（あとで setAvailability 可）
 * @property {string=} defaultLinkLabel - リンク文言の既定値（省略時 "空室状況"）
 * @property {(viewDate: Date) => void=} onMonthChange - 月変更時コールバック
 */

/**
 * 予約カレンダー本体
 */
export class ReservationCalendar {
  /** @param {ReservationCalendarOptions} opts */
  constructor(opts) {
    // DOM必須チェック
    if (!opts?.calendarEl || !opts?.ymSelectEl || !opts?.prevBtnEl || !opts?.nextBtnEl) {
      throw new Error("ReservationCalendar: 必要なDOM要素(calendarEl/ymSelectEl/prevBtnEl/nextBtnEl)が不足しています。");
    }

    /** @private */ this.calendarEl = opts.calendarEl;
    /** @private */ this.ymSelectEl = opts.ymSelectEl;
    /** @private */ this.prevBtnEl = opts.prevBtnEl;
    /** @private */ this.nextBtnEl = opts.nextBtnEl;
    /** @private */ this.loadingEl = opts.loadingEl || null;

    // 人数選択フォーム
    /** @private */ this.adultsSelectEl = opts.adultsSelectEl || null;
    /** @private */ this.childrenSelectEl = opts.childrenSelectEl || null;
    /** @private */ this.infantsSelectEl = opts.infantsSelectEl || null;

    // 人数のデフォルト値（フォームが存在する場合は初期値を読み取る）
    /** @private */ this.adults = this.adultsSelectEl ? Number(this.adultsSelectEl.value) : 2;
    /** @private */ this.children = this.childrenSelectEl ? Number(this.childrenSelectEl.value) : 0;
    /** @private */ this.infants = this.infantsSelectEl ? Number(this.infantsSelectEl.value) : 0;
    /** @private */ this.rooms = 1;

    /** @private */ this.monthsAhead = Number.isFinite(opts.monthsAhead) ? Number(opts.monthsAhead) : 7;
    /** @private */ this.today = opts.today instanceof Date ? opts.today : new Date();
    /** @private */ this.defaultLinkLabel = opts.defaultLinkLabel || DEFAULT_LINK_LABEL;

    /** @private */ this.availabilityMap = toAvailabilityMap(opts.availability || []);
    /** @private @type {Map<string,{isHoliday:boolean,isGameday:boolean,gameTimePattern:string|null}>} */
    this.holidayMap = opts.holidayMap instanceof Map ? opts.holidayMap : new Map();
    /** @private */ this.viewDate = new Date(this.today.getFullYear(), this.today.getMonth(), 1);
    /** @private */ this.range = getRangeLimits(this.today, this.monthsAhead);

    /** @private */ this.loadingGeneration = 0;
    /** @private */ this.onMonthChange = typeof opts.onMonthChange === "function" ? opts.onMonthChange : null;

    // バインド
    /** @private */ this._handlePrev = this._handlePrev.bind(this);
    /** @private */ this._handleNext = this._handleNext.bind(this);
    /** @private */ this._handleYmChange = this._handleYmChange.bind(this);
    /** @private */ this._handleGuestChange = this._handleGuestChange.bind(this);
  }

  /** 初期化（イベント登録 & 初回描画） */
  init() {
    this._setupYmOptions();
    this._syncYmSelect();
    this.prevBtnEl.addEventListener("click", this._handlePrev);
    this.nextBtnEl.addEventListener("click", this._handleNext);
    this.ymSelectEl.addEventListener("change", this._handleYmChange);

    // 人数選択フォームのイベントリスナー
    if (this.adultsSelectEl) {
      this.adultsSelectEl.addEventListener("change", this._handleGuestChange);
    }
    if (this.childrenSelectEl) {
      this.childrenSelectEl.addEventListener("change", this._handleGuestChange);
    }
    if (this.infantsSelectEl) {
      this.infantsSelectEl.addEventListener("change", this._handleGuestChange);
    }

    this.render();
  }

  /** 破棄（イベント解除） */
  destroy() {
    this.prevBtnEl.removeEventListener("click", this._handlePrev);
    this.nextBtnEl.removeEventListener("click", this._handleNext);
    this.ymSelectEl.removeEventListener("change", this._handleYmChange);

    // 人数選択フォームのイベントリスナー解除
    if (this.adultsSelectEl) {
      this.adultsSelectEl.removeEventListener("change", this._handleGuestChange);
    }
    if (this.childrenSelectEl) {
      this.childrenSelectEl.removeEventListener("change", this._handleGuestChange);
    }
    if (this.infantsSelectEl) {
      this.infantsSelectEl.removeEventListener("change", this._handleGuestChange);
    }
  }

  /**
   * 可視月の上限を変更（当月基準で再計算）
   * @param {number} monthsAhead
   */
  setMonthsAhead(monthsAhead) {
    this.monthsAhead = Number(monthsAhead);
    this.range = getRangeLimits(this.today, this.monthsAhead);
    this._setupYmOptions();
    this._clampViewDate();
    this._syncYmSelect();
    this.render();
  }

  /**
   * 今日基準日を変更（テスト等）
   * @param {Date} today
   */
  setToday(today) {
    this.today = today instanceof Date ? today : new Date(today);
    this.range = getRangeLimits(this.today, this.monthsAhead);
    this._setupYmOptions();
    this._clampViewDate();
    this._syncYmSelect();
    this.render();
  }

  /**
   * データ差し替え
   * @param {AvailabilityItem[]} rows
   */
  setAvailability(rows) {
    this.availabilityMap = toAvailabilityMap(rows || []);
    this.render();
  }

  /**
   * 休日・試合日データを差し替え
   * @param {Map<string,{isHoliday:boolean,isGameday:boolean,gameTimePattern:string|null}>} map
   */
  setHolidayMap(map) {
    this.holidayMap = map instanceof Map ? map : new Map();
    this.render();
  }

  /** 現在の状態を参照 */
  getState() {
    return {
      viewDate: new Date(this.viewDate),
      today: new Date(this.today),
      range: { min: new Date(this.range.min), max: new Date(this.range.max) },
      monthsAhead: this.monthsAhead,
      items: this.availabilityMap,
    };
  }

  /** 月を明示的に変更 */
  goTo(date) {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    this.viewDate = this._clampMonth(d);
    this._syncYmSelect();
    this._withLoading(() => this.render());
    if (this.onMonthChange) this.onMonthChange(this.viewDate);
  }

  /** 次の月へ */
  next() {
    this.goTo(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1));
  }

  /** 前の月へ */
  prev() {
    this.goTo(new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1));
  }

  /** 描画（同期処理だがローディング適用時は _withLoading 経由推奨） */
  render() {
    this._renderCalendar();
    this._syncNavButtons();
  }

  /* ====== 内部実装 ====== */

  /** 年月セレクトを範囲で構築 */
  _setupYmOptions() {
    const { min, max } = this.range;
    this.ymSelectEl.innerHTML = "";
    const startY = min.getFullYear();
    const startM = min.getMonth();
    const endY = max.getFullYear();
    const endM = max.getMonth();
    const total = (endY - startY) * 12 + (endM - startM) + 1; // inclusive

    for (let i = 0; i < total; i++) {
      const d = new Date(startY, startM + i, 1);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const opt = document.createElement("option");
      opt.value = `${y}-${String(m).padStart(2, "0")}-01`;
      opt.textContent = `${y}/${m}`;
      this.ymSelectEl.appendChild(opt);
    }
  }

  /** セレクトの値を viewDate に同期 */
  _syncYmSelect() {
    const y = this.viewDate.getFullYear();
    const m = String(this.viewDate.getMonth() + 1).padStart(2, "0");
    this.ymSelectEl.value = `${y}-${m}-01`;
  }

  /** viewDate を範囲に丸める */
  _clampViewDate() {
    this.viewDate = this._clampMonth(this.viewDate);
  }

  /** 範囲クランプ */
  _clampMonth(candidate) {
    const { min, max } = this.range;
    const c = new Date(candidate.getFullYear(), candidate.getMonth(), 1);
    const mMin = new Date(min.getFullYear(), min.getMonth(), 1);
    const mMax = new Date(max.getFullYear(), max.getMonth(), 1);
    if (c < mMin) return mMin;
    if (c > mMax) return mMax;
    return c;
  }

  /** 前後ボタンの有効/無効 */
  _syncNavButtons() {
    const { min, max } = this.range;
    const atMin = this.viewDate.getFullYear() === min.getFullYear() && this.viewDate.getMonth() === min.getMonth();
    const atMax = this.viewDate.getFullYear() === max.getFullYear() && this.viewDate.getMonth() === max.getMonth();
    this.prevBtnEl.disabled = atMin;
    this.nextBtnEl.disabled = atMax;
  }

  /** カレンダー本体の描画 */
  _renderCalendar() {
    const root = this.calendarEl;
    root.innerHTML = "";

    const { first, startPad, totalDays } = getMonthMeta(this.viewDate.getFullYear(), this.viewDate.getMonth());

    // 曜日ヘッダ
    for (const d of ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"]) {
      const el = document.createElement("div");
      el.className = "dow";
      el.textContent = d;
      root.appendChild(el);
    }

    const today0 = startOfDay(this.today);

    // 前月埋め草
    for (let i = 0; i < startPad; i++) {
      const el = document.createElement("div");
      el.className = "cell out";
      root.appendChild(el);
    }

    // 今月の日
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(first.getFullYear(), first.getMonth(), day);
      const key = toKey(date);
      const cell = document.createElement("div");
      cell.className = "cell";

      const date0 = startOfDay(date);
      const isToday = toKey(date0) === toKey(today0);
      const isPast = date0 < today0; // 当日は含める（過去のみ非活性）

      // if (isToday) cell.classList.add("today");
      if (isPast) cell.classList.add("past");

      const dateEl = document.createElement("div");
      dateEl.className = "date";
      dateEl.textContent = String(day);
      cell.appendChild(dateEl);

      // 祝日・試合日メタ
      const holidayMeta = this.holidayMap.get(key);
      const isHoliday = Boolean(holidayMeta?.isHoliday);
      const isGameday = Boolean(holidayMeta?.isGameday);
      if (isHoliday) {
        // 日付色変更用クラス
        dateEl.classList.add("is-holiday");
      }

      // 過去以外（= 今日・未来）に空室表示
      const info = this.availabilityMap.get(key);

      if (info && !isPast) {
        const { cls, aria, img } = statusMeta(info);


        const imgEl = document.createElement("img");
        imgEl.src = `./assets/img/${img}.svg`;
        imgEl.alt = aria;
        imgEl.width = img === "circle" ? 40 : 22;
        imgEl.height = img === "circle" ? 40 : 22;
        imgEl.classList.add(img === "circle" ? "img-circle" : "img-cross");

        cell.classList.add(img);

        const badge = document.createElement("span");
        badge.className = `badge badge--${cls}`;
        badge.textContent = info.status;
        badge.setAttribute("aria-label", `${key} の空室状況: ${aria}`);
        // cell.appendChild(badge);

        if (info.availableCount) {
          const a = document.createElement("a");
          a.className = "link";
          // check_outはcheck_inの日付+1日を設定
          const checkInDate = new Date(key);
          const checkOutDate = new Date(checkInDate);
          checkOutDate.setDate(checkInDate.getDate() + 1);
          const checkOutFormatted = checkOutDate.toISOString().split("T")[0];

          // 人数パラメータを含めたURL生成
          a.href = `https://booking.hkdballpark.com/tower11_hotel/fieldview/plans?check_in=${key}&check_out=${checkOutFormatted}&adults=${this.adults}&children=${this.children}&infants=${this.infants}&rooms=${this.rooms}`;
          a.href = `https://booking.hkdballpark.com/tower11_hotel/rooms?check_in=${key}&check_out=${checkOutFormatted}&adults=${this.adults}&children=${this.children}&infants=${this.infants}&rooms=${this.rooms}`;

          a.target = "_blank";
          a.rel = "noopener noreferrer";
          // a.textContent = info.label || this.defaultLinkLabel;
          a.setAttribute("aria-label", `${key} の${aria}ページへ`);
          // a.appendChild(badge);
          a.appendChild(imgEl);
          // 試合日の場合は右上に小さなアイコンを重ねる
          if (isGameday) {
            const gameIcon = document.createElement("img");
            gameIcon.className = "gameday-icon"; // CSSで背景画像や位置を指定
            gameIcon.src = `./assets/img/gameday.svg`;
            gameIcon.alt = `${key} は試合日`;
            gameIcon.setAttribute("aria-label", `${key} は試合日`);
            gameIcon.width = 30;
            gameIcon.height = 30;
            a.appendChild(gameIcon);
            cell.classList.add("gameday");
            cell.appendChild(a);
          } else {
            cell.appendChild(a);
          }
        } else {
          cell.appendChild(imgEl);
        }
      } else {
        const imgEl = document.createElement("img");
        imgEl.src = `./assets/img/bar.svg`;
        imgEl.alt = key;
        imgEl.width = 22;
        imgEl.height = 22;

        cell.classList.add("bar");
        cell.appendChild(imgEl);
      }

      root.appendChild(cell);
    }

    // 末尾埋め草で段をそろえる
    const cellsCount = root.children.length;
    const rem = cellsCount % 7;
    if (rem !== 0) {
      const pad = 7 - rem;
      for (let i = 0; i < pad; i++) {
        const el = document.createElement("div");
        el.className = "cell out";
        root.appendChild(el);
      }
    }
  }

  /* ===== ローディング（世代管理付き） ===== */

  /** @param {boolean} on @param {number=} ticket */
  _setLoading(on, ticket) {
    if (!this.loadingEl) return;
    const cal = this.calendarEl;

    // 古いチケットのOFFは無視
    if (on === false && typeof ticket === "number" && ticket !== this.loadingGeneration) {
      return;
    }
    this.loadingEl.hidden = !on;
    this.loadingEl.setAttribute("aria-hidden", String(!on));
    cal.setAttribute("aria-busy", String(on));
  }

  /**
   * @template T
   * @param {() => T} doSyncWork
   * @param {number} [minMs=400]
   * @returns {Promise<T>}
   */
  async _withLoading(doSyncWork, minMs = 400) {
    const myTicket = ++this.loadingGeneration;
    this._setLoading(true, myTicket);
    const t0 = performance.now();

    /** @type {T} */
    let result;
    try {
      result = doSyncWork();
    } catch (err) {
      console.error("[ReservationCalendar] render error:", err);
      const elapsed = performance.now() - t0;
      const wait = Math.max(0, minMs - elapsed);
      await new Promise((r) => setTimeout(r, wait));
      this._setLoading(false, myTicket);
      throw err;
    }

    const elapsed = performance.now() - t0;
    const wait = Math.max(0, minMs - elapsed);
    await new Promise((r) => setTimeout(r, wait));
    this._setLoading(false, myTicket);
    return result;
  }

  /* ===== イベントハンドラ ===== */

  _handlePrev() {
    const d = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.viewDate = this._clampMonth(d);
    this._withLoading(() => {
      this._syncYmSelect();
      this.render();
    });
    if (this.onMonthChange) this.onMonthChange(this.viewDate);
  }

  _handleNext() {
    const d = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.viewDate = this._clampMonth(d);
    this._withLoading(() => {
      this._syncYmSelect();
      this.render();
    });
    if (this.onMonthChange) this.onMonthChange(this.viewDate);
  }

  _handleYmChange() {
    const [y, m] = this.ymSelectEl.value.split("-").map(Number); // YYYY, MM, 01
    const d = new Date(y, m - 1, 1);
    this.viewDate = this._clampMonth(d);
    this._withLoading(() => this.render());
    if (this.onMonthChange) this.onMonthChange(this.viewDate);
  }

  /** 人数選択変更時 */
  _handleGuestChange() {
    if (this.adultsSelectEl) {
      this.adults = Number(this.adultsSelectEl.value);
    }
    if (this.childrenSelectEl) {
      this.children = Number(this.childrenSelectEl.value);
    }
    if (this.infantsSelectEl) {
      this.infants = Number(this.infantsSelectEl.value);
    }
    // 人数変更時はカレンダーを再描画（URLを更新するため）
    this.render();
  }
}

/**
 * ファクトリ（お好みで）
 * @param {ReservationCalendarOptions} opts
 * @returns {ReservationCalendar}
 */
export function createReservationCalendar(opts) {
  return new ReservationCalendar(opts);
}
